import { useState } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './context/AuthContext';
import { LayoutProvider, useLayout } from './context/LayoutContext';
import HealthIndicator from './components/HealthIndicator';
import ThemeToggle from './components/ThemeToggle';
import LanguageSwitcher from './components/LanguageSwitcher';
import ErrorBoundary from './components/ErrorBoundary';
import UserMenu from './components/user/UserMenu';
import UsageIndicator from './components/user/UsageIndicator';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import BottomNavBar from './components/mobile/BottomNavBar';
import MobileNav, { MobileMenuButton } from './components/mobile/MobileNav';
import PolishDataBanner from './components/PolishDataBanner';
import CookieConsent from './components/CookieConsent';
import AdminLayout from './components/admin/AdminLayout';
import LandingPage from './pages/LandingPage';
import SearchPage from './pages/SearchPage';
import AnalysisPage from './pages/AnalysisPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UsagePage from './pages/UsagePage';
import BookmarksPage from './pages/BookmarksPage';
import PricingPage from './pages/PricingPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import ProfilePage from './pages/ProfilePage';
import SharedJudgmentPage from './pages/SharedJudgmentPage';
import SharedCollectionPage from './pages/SharedCollectionPage';
import SharedAnalysisPage from './pages/SharedAnalysisPage';
import AnalysisHistoryPage from './pages/AnalysisHistoryPage';
import AnalysisComparisonPage from './pages/AnalysisComparisonPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogArticlePage from './pages/BlogArticlePage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import {
  AdminDashboard,
  AdminUsersPage,
  AdminAnalyticsPage,
  AdminSystemPage,
  AdminAuditPage,
  AdminDeepAnalysisPage,
  AdminModelsPage,
  AnalysisReviewPage,
} from './pages/admin';
import './index.css';

function MainLayout({ children }: { children: React.ReactNode }) {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const { isFullHeight } = useLayout();
  const { t } = useTranslation();

  return (
    <div className={`bg-slate-50 dark:bg-slate-900 transition-colors ${
      isFullHeight
        ? 'h-screen flex flex-col overflow-hidden'
        : 'min-h-screen pb-16 md:pb-0'
    }`}>
      {/* Mobile Navigation Drawer */}
      <MobileNav isOpen={isMobileNavOpen} onClose={() => setIsMobileNavOpen(false)} />

      {/* Header */}
      <header className={`bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 z-40 ${
        isFullHeight ? 'flex-shrink-0' : 'sticky top-0'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 md:gap-6">
              {/* Mobile menu button */}
              <MobileMenuButton onClick={() => setIsMobileNavOpen(true)} />

              <NavLink to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">{t('brand.name')}</h1>
              </NavLink>
              {/* Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                <NavLink
                  to="/search"
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`
                  }
                >
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {t('nav.search')}
                  </div>
                </NavLink>
                <NavLink
                  to="/analysis"
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`
                  }
                >
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    {t('nav.aiAnalysis')}
                  </div>
                </NavLink>
                <NavLink
                  to="/blog"
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`
                  }
                >
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                    Blog
                  </div>
                </NavLink>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <UsageIndicator />
              <LanguageSwitcher />
              <ThemeToggle />
              <HealthIndicator />
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Polish data info banner for English users */}
      <PolishDataBanner />

      {/* Main content */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${
        isFullHeight
          ? 'flex-1 flex flex-col min-h-0 py-4 w-full'
          : 'py-8'
      }`}>
        {children}
      </main>

      {/* Footer - hidden on mobile due to bottom nav */}
      <footer className={`hidden md:block bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 ${
        isFullHeight ? 'flex-shrink-0' : 'mt-16'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            {t('footer.text')}
          </p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <NavLink
              to="/regulamin"
              className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              {t('legal.footer.terms')}
            </NavLink>
            <span className="text-xs text-slate-300 dark:text-slate-600">|</span>
            <NavLink
              to="/polityka-prywatnosci"
              className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              {t('legal.footer.privacy')}
            </NavLink>
          </div>
        </div>
      </footer>

      {/* Bottom Navigation Bar - mobile only */}
      <BottomNavBar />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();

  // Check if we're on a shared page (these have their own layout)
  const isSharedPage = location.pathname.startsWith('/shared/');
  const isAdminPage = location.pathname.startsWith('/admin');

  if (isSharedPage) {
    return (
      <Routes>
        <Route path="/shared/judgment/:token" element={<SharedJudgmentPage />} />
        <Route path="/shared/collection/:token" element={<SharedCollectionPage />} />
        <Route path="/shared/analysis/:token" element={<SharedAnalysisPage />} />
      </Routes>
    );
  }

  if (isAdminPage) {
    return (
      <AdminRoute>
        <Routes>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="system" element={<AdminSystemPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
            <Route path="deep-analysis" element={<AdminDeepAnalysisPage />} />
            <Route path="models" element={<AdminModelsPage />} />
            <Route path="analysis-review" element={<AnalysisReviewPage />} />
          </Route>
        </Routes>
      </AdminRoute>
    );
  }

  return (
    <MainLayout>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route
          path="/analysis"
          element={
            <ProtectedRoute>
              <AnalysisPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analysis/history"
          element={
            <ProtectedRoute>
              <AnalysisHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analysis/compare"
          element={
            <ProtectedRoute>
              <AnalysisComparisonPage />
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/regulamin" element={<TermsOfServicePage />} />
        <Route path="/polityka-prywatnosci" element={<PrivacyPolicyPage />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/usage"
          element={
            <ProtectedRoute>
              <UsagePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookmarks"
          element={
            <ProtectedRoute>
              <BookmarksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/success"
          element={
            <ProtectedRoute>
              <CheckoutSuccessPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </MainLayout>
  );
}

function App() {
  return (
    <AuthProvider>
      <LayoutProvider>
        <ErrorBoundary>
          <AppRoutes />
          <CookieConsent />
        </ErrorBoundary>
      </LayoutProvider>
    </AuthProvider>
  );
}

export default App;
